import type { ReactNode } from 'react';
import { CheckCircle2, Circle, Copy, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getPlatformSetup, WEBHOOK_PLATFORMS } from './webhook-setup-content';

export type WebhookGuideVariant = 'onboarding' | 'settings';

interface WebhookConnectionGuideProps {
  /** Where the guide is shown — adjusts copy for post-onboarding Settings links. */
  variant: WebhookGuideVariant;
  /** Selected WordPress form platform (ELEMENTOR, GRAVITY_FORMS, …). */
  platform: string;
  /** True after POST /webhook/config has created a URL for this org. */
  urlGenerated: boolean;
  /** Full webhook URL including secret (when available). */
  webhookUrl?: string;
  onCopyUrl?: () => void;
  /** Optional slot for Generate URL button on step 1 (onboarding / pre-config settings). */
  generateAction?: ReactNode;
}

function StepMarker({ done }: { done: boolean }) {
  return done ? (
    <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0" aria-hidden="true" />
  ) : (
    <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
  );
}

/**
 * End-to-end connection guide: LarkPM webhook URL → WordPress form plugin → verify lead intake.
 * Used in Settings → Website Lead Integration and onboarding Step 3 (Configuration).
 */
export default function WebhookConnectionGuide({
  variant,
  platform,
  urlGenerated,
  webhookUrl,
  onCopyUrl,
  generateAction,
}: WebhookConnectionGuideProps) {
  const platformInfo = getPlatformSetup(platform);
  const platformLabel =
    WEBHOOK_PLATFORMS.find((p) => p.value === platform)?.label ?? 'your form plugin';

  const settingsPath =
    variant === 'onboarding'
      ? 'Settings → Website Lead Integration'
      : 'this page';

  return (
    <Card className="border border-outline-variant">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">
          Connection setup guide
        </CardTitle>
        <CardDescription>
          Follow these steps in order. Your website sends each form submission to LarkPM;
          a new lead is created automatically with source and campaign details when available.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* ── Phase 1: LarkPM ───────────────────────────────────── */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              Phase 1 · LarkPM
            </Badge>
            <span className="text-sm font-medium text-foreground">Generate your webhook URL</span>
          </div>

          <ol className="space-y-3 text-sm text-foreground-soft">
            <li className="flex gap-3">
              <StepMarker done={urlGenerated} />
              <div className="space-y-2 min-w-0 flex-1">
                <p>
                  <span className="font-medium text-foreground">Choose your form platform</span>
                  {' '}— currently selected: <strong className="text-foreground">{platformLabel}</strong>.
                  Change the platform dropdown above if your WordPress site uses a different plugin.
                </p>
              </div>
            </li>

            <li className="flex gap-3">
              <StepMarker done={urlGenerated} />
              <div className="space-y-2 min-w-0 flex-1">
                <p>
                  <span className="font-medium text-foreground">Generate the webhook URL</span>
                  {' '}— LarkPM creates a unique URL and secret for your company. The secret in the URL
                  authenticates submissions so only your website can create leads.
                </p>
                {!urlGenerated && generateAction && (
                  <div className="pt-1">{generateAction}</div>
                )}
                {urlGenerated && webhookUrl && (
                  <div className="rounded-lg border border-outline-variant bg-muted/40 p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Your webhook URL
                    </p>
                    <code className="block text-xs break-all text-foreground font-mono">
                      {webhookUrl}
                    </code>
                    {onCopyUrl && (
                      <Button type="button" variant="outline" size="sm" onClick={onCopyUrl}>
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        Copy webhook URL
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Copy the <strong>entire</strong> URL including the <code className="text-xs">?secret=</code> portion.
                      If you regenerate the secret later, update WordPress within 24 hours.
                    </p>
                  </div>
                )}
              </div>
            </li>
          </ol>
        </section>

        {/* ── Phase 2: WordPress ────────────────────────────────── */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              Phase 2 · WordPress
            </Badge>
            <span className="text-sm font-medium text-foreground">{platformInfo.title}</span>
          </div>

          {!urlGenerated && (
            <p className="text-sm text-muted-foreground pl-8">
              Complete Phase 1 first — you will paste the webhook URL into WordPress in the steps below.
            </p>
          )}

          <ol className="list-decimal list-inside space-y-2 text-sm text-foreground-soft pl-2">
            {platformInfo.steps.map((step, i) => (
              <li key={i} className="leading-relaxed pl-1">
                {step.split('**').map((part, j) =>
                  j % 2 === 1 ? (
                    <strong key={j} className="text-foreground font-medium">
                      {part}
                    </strong>
                  ) : (
                    part
                  ),
                )}
              </li>
            ))}
          </ol>

          {platformInfo.note && (
            <p className="text-xs text-warning bg-warning/10 border border-warning/20 rounded-lg p-3 ml-2">
              {platformInfo.note}
            </p>
          )}
        </section>

        {/* ── Phase 3: Verify ─────────────────────────────────────── */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              Phase 3 · Verify
            </Badge>
            <span className="text-sm font-medium text-foreground">Confirm leads are flowing in</span>
          </div>

          <ol className="space-y-3 text-sm text-foreground-soft">
            <li className="flex gap-3">
              <StepMarker done={false} />
              <p>
                <span className="font-medium text-foreground">Submit a test form</span> on your
                live website (include a real-looking name or phone — LarkPM needs at least one).
                Wait a few seconds for the webhook to fire.
              </p>
            </li>
            <li className="flex gap-3">
              <StepMarker done={false} />
              <div>
                <p className="font-medium text-foreground mb-1">Check the delivery log</p>
                {variant === 'settings' ? (
                  <p>
                    Scroll to <strong>Recent Deliveries</strong> on this page. A row with status{' '}
                    <strong>Success</strong> means LarkPM received and created a lead. If you see{' '}
                    <strong>Auth Failed</strong>, the secret in WordPress does not match — re-copy the URL.
                    <strong> Validation Failed</strong> usually means both name and phone were missing in the payload.
                  </p>
                ) : (
                  <p>
                    After you finish onboarding, open <strong>{settingsPath}</strong> and check{' '}
                    <strong>Recent Deliveries</strong> for a <strong>Success</strong> row. A failed row
                    includes an error reason (wrong secret, missing name and phone, etc.).
                  </p>
                )}
              </div>
            </li>
            <li className="flex gap-3">
              <StepMarker done={false} />
              <div>
                <p className="font-medium text-foreground mb-1">Review field mappings</p>
                {variant === 'settings' ? (
                  <p>
                    On the first real submission, LarkPM auto-maps your form field names to lead columns.
                    Review the <strong>Field Mappings</strong> section below — ensure{' '}
                    <strong>Name</strong> and <strong>Phone</strong> map correctly. Unmapped fields are
                    stored on the lead as custom data.
                  </p>
                ) : (
                  <p>
                    On the first submission, LarkPM auto-maps form fields. After onboarding, open{' '}
                    <strong>{settingsPath}</strong> to review or fix mappings (especially Name and Phone).
                  </p>
                )}
              </div>
            </li>
            <li className="flex gap-3">
              <StepMarker done={false} />
              <div>
                <p className="font-medium text-foreground mb-1">Open the lead in LarkPM</p>
                <p>
                  Go to <strong>Leads</strong> in the main navigation. The test lead should appear with{' '}
                  <strong>Entry method: Website form</strong>. Website leads start <strong>unassigned</strong>
                  — if you do not see it, log in as a receptionist or an owner who can create leads and
                  reassign reps (unassigned rows are hidden from other roles). On the lead detail page,
                  check <strong>Lead details</strong> for landing page, referrer, and campaign when your
                  form or Elementor sends that metadata. A second submit with the same email updates the
                  existing lead instead of creating a duplicate.
                </p>
                {variant === 'settings' && (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 mt-1 text-primary"
                    asChild
                  >
                    <a href="/leads">
                      Go to Leads
                      <ExternalLink className="h-3.5 w-3.5 ml-1 inline" />
                    </a>
                  </Button>
                )}
              </div>
            </li>
          </ol>
        </section>

        {/* ── Reference ─────────────────────────────────────────── */}
        <div className="rounded-lg bg-primary/5 border border-primary/15 p-4 text-xs text-foreground-soft space-y-2">
          <p className="font-semibold text-foreground text-sm">What LarkPM expects</p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Required:</strong> at least one of <strong>name</strong> or <strong>phone</strong>{' '}
              (labels can vary — mapping resolves them).
            </li>
            <li>
              <strong>Recommended:</strong> email, message, project type, budget, and lead source field.
            </li>
            <li>
              <strong>Automatic attribution:</strong> landing page URL, referrer, and campaign (UTM) when
              sent by Elementor or hidden form fields.
            </li>
            <li>
              <strong>Manual leads</strong> created inside LarkPM are unchanged — this connection only affects
              website form submissions.
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
